import { useMemo, useState, type FormEvent } from 'react'
import {
  useLibraryActivities,
  useCreateLibraryActivity,
  usePatchLibraryActivity,
  useDeleteLibraryActivity
} from '../hooks/useLibraryActivities'
import {
  ModalShell, Field, inputClass,
  CancelButton, PrimaryButton, DangerButton, ServerError
} from '../components/modal-form'
import { Icon } from '../components/Icon'
import { useMe } from '../hooks/useMe'
import {
  LIBRARY_ACTIVITY_CATEGORIES,
  type LibraryActivity,
  type LibraryActivityCategory,
  type LibraryActivityCreate,
  type LibraryActivityPatch
} from '../types'

// AdminLibraryActivities — admin-only page for the org-level Activities
// catalog (mig 035 / Library Build PR B). Mirrors AdminLibraryComponents:
// grouped by category, click to edit, soft-delete preserves any per-event
// Activities rows that came from a slug. Activity-specific defaults
// (location / time / staff / supplier) live alongside the standard fields
// and snapshot into the per-event live columns at copy time.

export function AdminLibraryActivities() {
  const { data: me } = useMe()
  const { data: activities, isLoading, error } = useLibraryActivities()
  const [editing, setEditing] = useState<LibraryActivity | 'new' | null>(null)

  // Group by category for visual scan; preserve LIBRARY_ACTIVITY_CATEGORIES
  // declared order so headers always appear in the canonical sequence.
  const grouped = useMemo(() => {
    const buckets = new Map<LibraryActivityCategory, LibraryActivity[]>()
    for (const a of activities ?? []) {
      const list = buckets.get(a.category) ?? []
      list.push(a)
      buckets.set(a.category, list)
    }
    return LIBRARY_ACTIVITY_CATEGORIES
      .map((cat) => ({ category: cat, rows: buckets.get(cat) ?? [] }))
      .filter((g) => g.rows.length > 0)
  }, [activities])

  if (me && !me.isAdmin) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="text-3xl mb-1">Activities library</h1>
          <p className="text-ink-soft">Admin only.</p>
        </div>
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          Editing the activities library requires admin permissions.
        </div>
      </div>
    )
  }

  const autoAddCount = activities?.filter((a) => a.autoAdd).length ?? 0

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Activities library</h1>
          <p className="text-ink-soft">
            Reusable programme items — entertainment, photo ops, garden games,
            workshops. Defaults set here (location, time, staff, supplier) seed
            the per-event values when an activity is added. Edits here do not
            change activities already on existing events.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="px-4 py-2 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90 transition-colors"
        >
          + New activity
        </button>
      </div>

      {isLoading && (
        <div className="text-ink-soft text-sm font-mono">Loading activities…</div>
      )}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load activities</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {activities && activities.length === 0 && (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#f5edd5', color: '#9b7f2a' }}
          >
            <Icon name="calendar" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No library activities yet</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            Add an activity here and it becomes available for every event
            to pull onto its Activities tab. Mark a row “auto-add” to have
            it copied to every new event automatically.
          </p>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
          >
            + Add the first activity
          </button>
        </div>
      )}

      {activities && activities.length > 0 && (
        <div className="text-xs text-ink-soft font-mono">
          {activities.length} activit{activities.length === 1 ? 'y' : 'ies'} · {autoAddCount} auto-add
        </div>
      )}

      {grouped.map(({ category, rows }) => (
        <section key={category} className="bg-white border border-line rounded overflow-hidden">
          <header className="px-4 py-2.5 border-b border-line bg-parchment/60 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">{category}</h2>
            <span className="text-xs text-ink-soft font-mono">
              {rows.length} activit{rows.length === 1 ? 'y' : 'ies'}
            </span>
          </header>
          <div className="divide-y divide-line">
            {rows.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setEditing(a)}
                className="w-full text-left px-4 py-3 flex items-start gap-4 hover:bg-parchment/40 transition-colors"
              >
                <Icon name="calendar" size={16} className="text-slate flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-semibold text-sm">{a.name}</span>
                    <span className="text-[10px] text-ink-soft font-mono">{a.slug}</span>
                  </div>
                  {a.description && (
                    <div className="text-xs text-ink-soft mt-0.5 line-clamp-1">
                      {a.description}
                    </div>
                  )}
                  {(a.defaultLocation || a.defaultTime || a.defaultStaff != null || a.defaultSupplier) && (
                    <div className="text-[11px] text-ink-soft mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {a.defaultLocation && <span>📍 {a.defaultLocation}</span>}
                      {a.defaultTime && <span className="font-mono">🕐 {a.defaultTime}</span>}
                      {a.defaultStaff != null && <span>{a.defaultStaff} staff</span>}
                      {a.defaultSupplier && <span className="italic">{a.defaultSupplier}</span>}
                    </div>
                  )}
                </div>
                {a.autoAdd && (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded whitespace-nowrap"
                    style={{ background: '#dcebe5', color: '#0a4a3a' }}
                  >
                    auto-add
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      ))}

      {editing && (
        <ActivityEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

// ---- Editor modal --------------------------------------------------------

function ActivityEditor({
  initial, onClose
}: {
  initial: LibraryActivity | null
  onClose: () => void
}) {
  const isEditing = initial != null
  const createMut = useCreateLibraryActivity()
  const patchMut  = usePatchLibraryActivity()
  const deleteMut = useDeleteLibraryActivity()

  const [slug, setSlug]                       = useState(initial?.slug ?? '')
  const [name, setName]                       = useState(initial?.name ?? '')
  const [category, setCategory]               = useState<LibraryActivityCategory>(initial?.category ?? 'Entertainment')
  const [icon, setIcon]                       = useState(initial?.icon ?? '')
  const [description, setDescription]         = useState(initial?.description ?? '')
  const [defaultLocation, setDefaultLocation] = useState(initial?.defaultLocation ?? '')
  const [defaultTime, setDefaultTime]         = useState(initial?.defaultTime ?? '')
  const [defaultStaff, setDefaultStaff]       = useState(initial?.defaultStaff?.toString() ?? '')
  const [defaultSupplier, setDefaultSupplier] = useState(initial?.defaultSupplier ?? '')
  const [autoAdd, setAutoAdd]                 = useState(initial?.autoAdd ?? false)

  // Auto-suggest slug from name on the CREATE path only — once a slug
  // exists (edit mode), don't touch it. Keeps the contract stable.
  function onNameChange(v: string) {
    setName(v)
    if (!isEditing && (slug === '' || slug === slugify(name))) {
      setSlug(slugify(v))
    }
  }

  const trimmedSlug = slug.trim()
  const trimmedName = name.trim()
  const fieldErrors: { slug?: string; name?: string; defaultStaff?: string } = {}
  if (!isEditing) {
    if (trimmedSlug.length === 0) fieldErrors.slug = 'Slug is required'
    else if (trimmedSlug.length > 80) fieldErrors.slug = 'Slug must be 80 chars or fewer'
    else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(trimmedSlug)) {
      fieldErrors.slug = 'Slug must be kebab-case (lowercase letters, digits, hyphens)'
    }
  }
  if (trimmedName.length === 0) fieldErrors.name = 'Name is required'
  else if (trimmedName.length > 200) fieldErrors.name = 'Name must be 200 chars or fewer'

  // defaultStaff is a free text input — empty = null, otherwise non-negative integer
  const trimmedStaff = defaultStaff.trim()
  let parsedStaff: number | null = null
  if (trimmedStaff.length > 0) {
    const n = Number(trimmedStaff)
    if (!Number.isInteger(n) || n < 0) {
      fieldErrors.defaultStaff = 'Staff must be a non-negative whole number'
    } else {
      parsedStaff = n
    }
  }

  const isValid = Object.keys(fieldErrors).length === 0
  const saving = createMut.isPending || patchMut.isPending
  const deleting = deleteMut.isPending
  const serverError = (createMut.error ?? patchMut.error ?? deleteMut.error) as Error | null

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    if (isEditing && initial) {
      const patch: LibraryActivityPatch = {
        name: trimmedName,
        category,
        icon: icon.trim() || null,
        description: description.trim() || null,
        defaultLocation: defaultLocation.trim() || null,
        defaultTime: defaultTime.trim() || null,
        defaultStaff: parsedStaff,
        defaultSupplier: defaultSupplier.trim() || null,
        autoAdd
      }
      patchMut.mutate({ id: initial.id, body: patch }, { onSuccess: () => onClose() })
    } else {
      const body: LibraryActivityCreate = {
        slug: trimmedSlug,
        name: trimmedName,
        category,
        icon: icon.trim() || null,
        description: description.trim() || null,
        defaultLocation: defaultLocation.trim() || null,
        defaultTime: defaultTime.trim() || null,
        defaultStaff: parsedStaff,
        defaultSupplier: defaultSupplier.trim() || null,
        autoAdd
      }
      createMut.mutate(body, { onSuccess: () => onClose() })
    }
  }

  function handleDelete() {
    if (!initial) return
    const ok = window.confirm(
      `Remove "${initial.name}" from the library? Activities already added to existing events stay — only future "Add from library" lookups stop seeing it.`
    )
    if (!ok) return
    deleteMut.mutate(initial.id, { onSuccess: () => onClose() })
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit library activity' : 'New library activity'}
      onClose={onClose}
      onSubmit={handleSubmit}
      size="lg"
      footer={
        <>
          {isEditing && (
            <DangerButton onClick={handleDelete} disabled={saving || deleting}>
              {deleting ? 'Removing…' : 'Remove'}
            </DangerButton>
          )}
          <CancelButton onClick={onClose} disabled={saving} />
          <PrimaryButton disabled={!isValid || saving}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add activity'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <Field label="Name" error={fieldErrors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              maxLength={200}
              autoFocus
              required
              className={inputClass(!!fieldErrors.name)}
              placeholder="e.g. Live entertainment"
            />
          </Field>

          <Field
            label="Slug"
            error={fieldErrors.slug}
            hint={isEditing ? 'Locked — slugs are the public contract' : 'Auto-suggested from name. Kebab-case.'}
          >
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              maxLength={80}
              required={!isEditing}
              disabled={isEditing}
              className={inputClass(!!fieldErrors.slug) + (isEditing ? ' opacity-60 cursor-not-allowed' : '')}
              placeholder="live-entertainment"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as LibraryActivityCategory)}
                className={inputClass(false)}
              >
                {LIBRARY_ACTIVITY_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Icon" hint="Lucide name (optional)">
              <input
                type="text"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                maxLength={40}
                className={inputClass(false)}
                placeholder="calendar"
              />
            </Field>
          </div>

          <Field label="Description (optional)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={inputClass(false)}
              placeholder="Short description shown on the per-event Activities tab."
            />
          </Field>

          <div className="border-t border-line pt-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-soft mb-3">
              Defaults — copied into per-event values when this activity is added
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Default location (optional)">
                <input
                  type="text"
                  value={defaultLocation}
                  onChange={(e) => setDefaultLocation(e.target.value)}
                  maxLength={200}
                  className={inputClass(false)}
                  placeholder="e.g. Front Lawn"
                />
              </Field>
              <Field label="Default time (optional)">
                <input
                  type="text"
                  value={defaultTime}
                  onChange={(e) => setDefaultTime(e.target.value)}
                  maxLength={120}
                  className={inputClass(false)}
                  placeholder="09:30 – 16:00"
                />
              </Field>
              <Field label="Default staff (optional)" error={fieldErrors.defaultStaff}>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={defaultStaff}
                  onChange={(e) => setDefaultStaff(e.target.value)}
                  className={inputClass(!!fieldErrors.defaultStaff)}
                  placeholder="0"
                />
              </Field>
              <Field label="Default supplier (optional)">
                <input
                  type="text"
                  value={defaultSupplier}
                  onChange={(e) => setDefaultSupplier(e.target.value)}
                  maxLength={200}
                  className={inputClass(false)}
                  placeholder="e.g. Phantom Productions"
                />
              </Field>
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoAdd}
              onChange={(e) => setAutoAdd(e.target.checked)}
              disabled={saving}
              className="w-4 h-4 mt-0.5 accent-green cursor-pointer disabled:cursor-not-allowed"
            />
            <div>
              <div className="text-sm font-semibold">Auto-add to new events</div>
              <div className="text-xs text-ink-soft">
                When ticked, this activity is automatically copied onto every
                new event when it's created. Existing events are NOT backfilled.
              </div>
            </div>
          </label>

          {serverError && <ServerError title="Could not save" error={serverError} />}
        </div>
      }
    />
  )
}

// kebab-case from a free-text name. Strips non-alphanumeric, collapses
// runs of dashes, trims leading/trailing dashes. Used only for the
// create path's slug auto-suggest — admin can edit before submit.
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
